import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';
import { absolutize } from '../components/MediaUrlsInput';
import { useLocalize } from '../i18n/useLocalize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Icon = ({ name, size = 32, className }) => {
  const Cmp = LucideIcons[name] || LucideIcons.Globe;
  return <Cmp size={size} className={className} />;
};

const About = () => {
  const [content, setContent] = useState(null);
  const { t } = useTranslation();
  const localize = useLocalize();

  useEffect(() => {
    axios.get(`${API}/about`)
      .then((res) => setContent(res.data))
      .catch(() => setContent({}));
  }, []);

  if (!content) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <p className="text-maroon/50">{t('common.loading')}</p>
      </div>
    );
  }

  // Localize all text fields; values are nested objects so handle separately
  const c = localize(content, [
    'hero_title', 'hero_subtitle',
    'story_title', 'story_paragraphs',
    'values_title', 'values',
    'mission_title', 'mission_text',
  ]);

  const {
    hero_title,
    hero_subtitle,
    story_image,
    story_title,
    story_paragraphs = [],
    values_title,
    values = [],
    mission_title,
    mission_text,
  } = c;

  return (
    <div className="py-16" data-testid="about-page">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl sm:text-6xl font-heading font-bold text-maroon mb-6">
            {hero_title}
          </h1>
          <p className="text-xl text-maroon/60 max-w-3xl mx-auto">
            {hero_subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            {story_image && (
              <img
                src={absolutize(story_image)}
                alt={story_title || 'Our Story'}
                className="rounded-sm border-2 border-maroon hard-shadow"
              />
            )}
          </div>
          <div>
            <h2 className="text-3xl font-heading font-bold text-maroon mb-4">
              {story_title}
            </h2>
            {story_paragraphs.map((p, idx) => (
              <p key={idx} className="text-maroon/60 mb-4 last:mb-0">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      {values.length > 0 && (
        <section className="bg-sand/30 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-heading font-bold text-maroon text-center mb-12">
              {values_title}
            </h2>

            <div className={`grid grid-cols-1 md:grid-cols-2 ${values.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-' + values.length} gap-8`}>
              {values.map((v, idx) => (
                <div key={idx} className="card text-center" data-testid={`about-value-${idx}`}>
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-terracotta/10 mb-4">
                    <Icon name={v.icon} size={32} className="text-terracotta" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-maroon mb-3">
                    {v.title}
                  </h3>
                  <p className="text-maroon/70">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mission Section */}
      {mission_text && (
        <section className="py-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-heading font-bold text-maroon mb-6">
            {mission_title}
          </h2>
          <p className="text-xl text-maroon/60 mb-8">
            {mission_text}
          </p>
        </section>
      )}
    </div>
  );
};

export default About;
